import { useEffect, useMemo, useState } from 'react'
import { logger } from '@/lib/logger'
import { showErrorData } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { useUploadEngineMedia } from '@/features/editor/hooks/use-upload-engine-media'
import { useEditorStore } from '@/features/editor/stores/editor-store'
import { useInvalidateEngineCache } from '../../hooks/use-editor-bootstrap'
import { CropDialog } from '../media/crop-dialog'
import { MediaTile } from '../media/media-tile'
import { SectionCard } from './section-card'

type UploadKind = 'cover' | 'background'
type CoverRatio = '1/1' | '3/4' | '4/3' | '16/9'
const MAX_MEDIA_BYTES = 5 * 1024 * 1024

function extFromMime(mime: string) {
  if (mime === 'image/jpeg') return 'jpg'
  if (mime === 'image/webp') return 'webp'
  return 'png'
}

function ratioToAspect(r: CoverRatio) {
  if (r === '1/1') return 1 / 1
  if (r === '3/4') return 3 / 4
  if (r === '4/3') return 4 / 3
  return 16 / 9
}

function ratioLabel(r: CoverRatio) {
  return r.replace('/', ':')
}

export function MediaSection() {
  const engineId = useEditorStore((s) => s.draft.engineId)
  const coverImageUrl = useEditorStore((s) => s.draft.coverImageUrl)
  const backgroundImageUrl = useEditorStore((s) => s.draft.backgroundImageUrl)
  const patchDraft = useEditorStore((s) => s.patchDraft)

  const { upload, uploadingCover, uploadingBackground } = useUploadEngineMedia()

  // ===== 裁剪弹窗状态 =====
  const [cropOpen, setCropOpen] = useState(false)
  const [cropKind, setCropKind] = useState<UploadKind>('cover')
  const [cropSrc, setCropSrc] = useState('')
  const [coverRatio, setCoverRatio] = useState<CoverRatio>('3/4') // 默认 3:4

  // ===== 本地预览（仅用于上传中）=====
  const [localPreviewCover, setLocalPreviewCover] = useState('')
  const [localPreviewBg, setLocalPreviewBg] = useState('')

  // 手动清除引擎缓存
  const { invalidate: invalidateEngineCache } = useInvalidateEngineCache()
  // ===== 统一清理 =====
  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc)
      if (localPreviewCover) URL.revokeObjectURL(localPreviewCover)
      if (localPreviewBg) URL.revokeObjectURL(localPreviewBg)
    }
  }, [cropSrc, localPreviewCover, localPreviewBg])

  function openCrop(kind: UploadKind, file: File, nextCoverRatio?: CoverRatio) {
    if (!engineId) return
    if (file.size > MAX_MEDIA_BYTES) {
      showErrorData('图片大小不能超过 5MB')
      return
    }

    const url = URL.createObjectURL(file)
    if (cropSrc) URL.revokeObjectURL(cropSrc)

    setCropKind(kind)
    setCropSrc(url)

    // 只有 cover 才用 ratio；background 固定 16:9
    if (kind === 'cover' && nextCoverRatio) {
      setCoverRatio(nextCoverRatio)
    }

    setCropOpen(true)
  }

  function closeCrop() {
    setCropOpen(false)
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc('')
  }

  async function confirmCrop(blob: Blob) {
    if (!engineId) return

    const mime = blob.type || 'image/png'
    const ext = extFromMime(mime)

    const name =
      cropKind === 'cover'
        ? `cover_${coverRatio.replace('/', '_')}.${ext}`
        : `background.${ext}`

    const file = new File([blob], name, { type: mime })

    // 本次上传对应的临时预览（用局部变量，避免竞态）
    const previewUrl = URL.createObjectURL(file)

    if (cropKind === 'cover') {
      if (localPreviewCover) URL.revokeObjectURL(localPreviewCover)
      setLocalPreviewCover(previewUrl)
    } else {
      if (localPreviewBg) URL.revokeObjectURL(localPreviewBg)
      setLocalPreviewBg(previewUrl)
    }
    invalidateEngineCache(engineId)

    try {
      const { url } = await upload({ engineId, kind: cropKind, file })

      // [DEBUG] 追踪上传返回的 URL
      logger.debug('[MediaSection] upload returned url:', url)
      logger.debug(
        '[MediaSection] current coverImageUrl in store:',
        coverImageUrl
      )
      logger.debug(
        '[MediaSection] current backgroundImageUrl in store:',
        backgroundImageUrl
      )

      if (cropKind === 'cover') {
        logger.debug('[MediaSection] patching coverImageUrl to:', url)
        patchDraft({ coverImageUrl: url })
      } else {
        logger.debug('[MediaSection] patching backgroundImageUrl to:', url)
        patchDraft({ backgroundImageUrl: url })
      }
    } finally {
      URL.revokeObjectURL(previewUrl)
      if (cropKind === 'cover') setLocalPreviewCover('')
      else setLocalPreviewBg('')
    }
  }

  function removeCover() {
    if (localPreviewCover) URL.revokeObjectURL(localPreviewCover)
    setLocalPreviewCover('')
    patchDraft({ coverImageUrl: '' })
  }

  function removeBackground() {
    if (localPreviewBg) URL.revokeObjectURL(localPreviewBg)
    setLocalPreviewBg('')
    patchDraft({ backgroundImageUrl: '' })
  }

  const aspect = cropKind === 'cover' ? ratioToAspect(coverRatio) : 16 / 9
  const dialogTitle =
    cropKind === 'cover'
      ? `裁剪封面（${ratioLabel(coverRatio)}）`
      : '裁剪背景（16:9）'
  const dialogSaving =
    cropKind === 'cover' ? uploadingCover : uploadingBackground

  const coverOptions = useMemo(
    () =>
      [
        { ratio: '1/1', label: '1:1' },
        { ratio: '3/4', label: '3:4' },
        { ratio: '4/3', label: '4:3' },
        { ratio: '16/9', label: '16:9' },
      ] as const,
    []
  )

  return (
    <SectionCard
      title='图片'
      description='悬浮图片可上传、替换或删除；上传后支持裁剪并自动写入草稿。图片不超过 5MB。'
    >
      {!engineId ? (
        <div className='text-muted-foreground text-sm'>
          当前没有 engineId，请先创建引擎后再上传封面与背景。
        </div>
      ) : null}

      <div className='grid grid-cols-1 gap-8 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start'>
        {/* 封面（支持 4 种比例） */}
        <div className='w-full space-y-3 lg:max-w-[320px]'>
          <div className='flex flex-wrap gap-2'>
            {coverOptions.map((opt) => {
              const active = opt.ratio === coverRatio
              return (
                <button
                  key={opt.ratio}
                  type='button'
                  disabled={!engineId || uploadingCover}
                  onClick={() => setCoverRatio(opt.ratio)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs transition',
                    active
                      ? 'border-primary/40 bg-primary/10'
                      : 'border-border hover:border-foreground/20',
                    !engineId || uploadingCover
                      ? 'cursor-not-allowed opacity-60'
                      : ''
                  )}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          <MediaTile
            inputId='editor-cover-upload'
            title={`封面（${ratioLabel(coverRatio)}）`}
            ratio={coverRatio}
            url={localPreviewCover || coverImageUrl || ''}
            disabled={!engineId}
            busy={uploadingCover}
            onPickFile={(file) => openCrop('cover', file, coverRatio)}
            onRemove={removeCover}
          />
        </div>

        {/* 背景（16:9） */}
        <div className='w-full'>
          <MediaTile
            inputId='editor-bg-upload'
            title='背景（16:9）'
            ratio='16/9'
            url={localPreviewBg || backgroundImageUrl || ''}
            disabled={!engineId}
            busy={uploadingBackground}
            onPickFile={(file) => openCrop('background', file)}
            onRemove={removeBackground}
          />
        </div>
      </div>

      <CropDialog
        open={cropOpen}
        title={dialogTitle}
        imageUrl={cropSrc}
        aspect={aspect}
        saving={dialogSaving}
        onClose={closeCrop}
        onConfirm={async (blob) => {
          await confirmCrop(blob)
          closeCrop()
        }}
      />
    </SectionCard>
  )
}
